import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Inbox, Loader2, Mail, Phone, RefreshCw, Search, Send, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? "s" : ""} ago`;
};

const STATUS_META = {
  new: { label: "Needs reply", cls: "bg-[#C89B5F]/15 text-[#8a6a3a]" },
  customer_replied: { label: "Guest replied", cls: "bg-[#B76E79]/15 text-[#B76E79]" },
  replied: { label: "Replied", cls: "bg-[#3B8C4E]/15 text-[#2A6636]" },
};

// Older enquiries predate threading — fall back to the original message.
const threadOf = (e) =>
  Array.isArray(e.thread) && e.thread.length
    ? e.thread
    : [{ direction: "in", body: e.message, subject: "Website enquiry", at: e.created_at }];

const AdminEnquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({}); // id -> { subject, body } (Justina's proof-read copy)
  const [notes, setNotes] = useState({}); // id -> optional instruction for a redraft
  const [busyDraft, setBusyDraft] = useState(null);
  const [busySend, setBusySend] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`);
      setItems(data || []);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? items
      : items.filter((e) => {
          const hay = [
            e.name, e.email, e.phone, e.accommodation, e.message,
            ...threadOf(e).map((t) => `${t.subject || ""} ${t.body || ""}`),
          ].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        });
    const activity = (e) => e.last_message_at || e.created_at || "";
    return [...filtered].sort((a, b) => activity(b).localeCompare(activity(a)));
  }, [items, search]);

  const draftFor = (e) => ({
    subject: e.draft_subject || "",
    body: e.draft_body || "",
    ...(edits[e.id] || {}),
  });

  const setDraft = (e, patch) =>
    setEdits((prev) => ({ ...prev, [e.id]: { ...draftFor(e), ...(prev[e.id] || {}), ...patch } }));

  const regenerate = async (e) => {
    setBusyDraft(e.id);
    try {
      const { data } = await axios.post(`${API}/admin/enquiries/${e.id}/draft`, {
        context: (notes[e.id] || "").trim() || null,
      });
      setEdits((prev) => ({ ...prev, [e.id]: { subject: data.subject, body: data.body } }));
      setNotes((prev) => ({ ...prev, [e.id]: "" }));
      toast.success("Fresh draft ready — proof-read it below.");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The AI drafter is unavailable right now.");
    } finally {
      setBusyDraft(null);
    }
  };

  const sendReply = async (e) => {
    const d = draftFor(e);
    if (!d.subject.trim() || !d.body.trim()) {
      toast.error("The reply needs a subject and a message.");
      return;
    }
    if (!window.confirm(`Send this reply to ${e.email}?`)) return;
    setBusySend(e.id);
    try {
      await axios.post(`${API}/admin/enquiries/${e.id}/reply`, { subject: d.subject.trim(), body: d.body });
      toast.success(`Reply sent to ${e.email}.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the reply.");
    } finally {
      setBusySend(null);
    }
  };

  return (
    <section data-testid="admin-enquiries">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-[#6A5F58]"><span className="marker-line" /> Guest conversations</p>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">Enquiries &amp; replies</h2>
          <p className="mt-3 text-[#6A5F58] text-sm max-w-xl">
            Contact-form messages and direct emails land here as conversations, each with an AI-drafted reply
            waiting. Tap a guest to open the whole thread, proof-read, and reply — guest answers arrive back
            here automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition"
          data-testid="enquiries-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6A5F58] pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, phone or message…"
          className="pl-9 bg-white h-11 rounded-full border-[#DDD1BE]"
          data-testid="enquiries-search"
        />
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[#6A5F58] text-sm" data-testid="enquiries-loading">
          <Loader2 size={16} className="animate-spin" /> Loading conversations…
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-10 text-center" data-testid="enquiries-empty">
          <Inbox size={28} className="mx-auto text-[#DDD1BE]" />
          <p className="mt-3 text-sm text-[#6A5F58]">
            {search ? `Nothing matches "${search}".` : "No enquiries yet — guest messages and emails will appear here with ready-drafted replies."}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="enquiries-list">
          {visible.map((e) => {
            const meta = STATUS_META[e.status] || STATUS_META.new;
            const thread = threadOf(e);
            const last = thread[thread.length - 1] || {};
            const isOpen = expanded === e.id;
            const d = draftFor(e);
            return (
              <div key={e.id} className="bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden" data-testid={`enquiry-card-${e.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : e.id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#FBF7F1] transition"
                  data-testid={`enquiry-expand-${e.id}`}
                >
                  {isOpen
                    ? <ChevronDown size={15} className="text-[#6A5F58] shrink-0" />
                    : <ChevronRight size={15} className="text-[#6A5F58] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-lg text-[#14100F]" data-testid={`enquiry-name-${e.id}`}>{e.name || "—"}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold ${meta.cls}`} data-testid={`enquiry-status-${e.id}`}>
                        {meta.label}
                      </span>
                      {thread.length > 1 && (
                        <span className="text-[9px] text-[#6A5F58]">{thread.length} messages</span>
                      )}
                      {e.source === "direct-email" && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] bg-[#5B7A8C]/15 text-[#5B7A8C] font-semibold">
                          direct email
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#6A5F58] truncate mt-0.5" data-testid={`enquiry-snippet-${e.id}`}>
                      {last.direction === "out" ? "You: " : ""}{last.body || e.message}
                    </p>
                  </div>
                  <span className="text-[10px] text-[#6A5F58] whitespace-nowrap shrink-0">
                    {timeAgo(e.last_message_at || e.created_at)}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-[#EDE3D2] p-5 md:p-6">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 text-[#B76E79] hover:underline" data-testid={`enquiry-email-${e.id}`}>
                        <Mail size={11} /> {e.email}
                      </a>
                      {e.phone && (
                        <a href={`tel:${(e.phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-[#0F0E0E] hover:text-[#B76E79]" data-testid={`enquiry-phone-${e.id}`}>
                          <Phone size={11} /> {e.phone}
                        </a>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">
                        {e.accommodation || "General enquiry"}
                        {(e.arrival_date || e.departure_date) && ` · ${e.arrival_date || "?"} → ${e.departure_date || "?"}`}
                        {e.guests ? ` · ${e.guests} guest${e.guests > 1 ? "s" : ""}` : ""}
                      </span>
                    </div>

                    <div className="mt-5 space-y-3" data-testid={`enquiry-thread-${e.id}`}>
                      {thread.map((m, i) => (
                        <div key={i} className={m.direction === "out" ? "ml-auto max-w-[85%]" : "mr-auto max-w-[85%]"}>
                          <div
                            className={
                              m.direction === "out"
                                ? "bg-[#0F0E0E] text-[#F5EFE6] rounded-2xl rounded-br-sm px-4 py-3"
                                : "bg-[#FBF7F1] border border-[#EDE3D2] text-[#3A322E] rounded-2xl rounded-bl-sm px-4 py-3"
                            }
                            data-testid={`enquiry-thread-${m.direction}-${e.id}-${i}`}
                          >
                            {m.subject && m.direction === "in" && (
                              <p className="text-[9px] uppercase tracking-[0.14em] text-[#6A5F58] mb-1">{m.subject}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                          </div>
                          <p className={`text-[9px] text-[#6A5F58] mt-1 ${m.direction === "out" ? "text-right" : ""}`}>
                            {m.direction === "out" ? "You" : (e.name || "Guest")} · {timeAgo(m.at)}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-5 border-t border-[#EDE3D2]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Sparkles size={13} className="text-[#B76E79]" />
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">
                          {d.body ? "AI draft — proof-read before sending" : "No draft yet — tap Redraft to create one"}
                        </p>
                        {e.draft_model && <span className="text-[9px] text-[#6A5F58]">· {e.draft_model}</span>}
                      </div>
                      <Input
                        value={d.subject}
                        onChange={(ev) => setDraft(e, { subject: ev.target.value })}
                        placeholder="Subject"
                        className="mt-3 bg-[#F5EFE6] h-11 rounded-lg"
                        data-testid={`enquiry-subject-${e.id}`}
                      />
                      <Textarea
                        value={d.body}
                        onChange={(ev) => setDraft(e, { body: ev.target.value })}
                        placeholder="Draft reply will appear here…"
                        rows={7}
                        className="mt-3 bg-[#F5EFE6] rounded-lg text-sm leading-relaxed"
                        data-testid={`enquiry-body-${e.id}`}
                      />
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Input
                          value={notes[e.id] || ""}
                          onChange={(ev) => setNotes((prev) => ({ ...prev, [e.id]: ev.target.value }))}
                          placeholder="Instruction for a redraft (optional) — e.g. 'offer weekdays instead'"
                          className="flex-1 min-w-[220px] bg-[#F5EFE6] h-10 rounded-lg text-xs"
                          data-testid={`enquiry-note-${e.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => regenerate(e)}
                          disabled={busyDraft === e.id}
                          className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
                          data-testid={`enquiry-redraft-${e.id}`}
                        >
                          {busyDraft === e.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                          Redraft
                        </button>
                        <button
                          type="button"
                          onClick={() => sendReply(e)}
                          disabled={busySend === e.id}
                          className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
                          data-testid={`enquiry-send-${e.id}`}
                        >
                          {busySend === e.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          Send reply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AdminEnquiries;
